import { api } from "@convex/_generated/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { twJoin } from "tailwind-merge";

import { DeviceRouteLoadingDialog } from "~/components/devices/device-route-loading-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { writePendingPosDeviceTerminalConfig } from "~/lib/pos-device-payment-terminal-storage";
import {
  EMPTY_POS_DEVICE_PAYMENT_TERMINAL,
  type PaymentType,
  type PosDevicePaymentTerminal,
  paymentTypeOptions,
  paymentTypes,
} from "~/lib/validators/pos-device-payment-terminal";

const getDeviceByIdRef = api.posDevices.getDeviceById;

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/locations/$id/devices/$deviceId/add-terminal"
)({
  component: AddTerminalModal,
});

function AddTerminalModal() {
  const { id: locationId, deviceId } = Route.useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [paymentTypeSelected, setPaymentTypeSelected] =
    useState<PaymentType>("stripe");

  const { data: device, isPending } = useQuery({
    queryKey: ["posDevices", "getDeviceById", deviceId],
    queryFn: () => {
      return convexClient.query(getDeviceByIdRef, {
        id: toConvexTableId("posDevices", deviceId),
      });
    },
  });

  const form = useForm<PosDevicePaymentTerminal>({
    defaultValues: {
      ...EMPTY_POS_DEVICE_PAYMENT_TERMINAL,
      type: paymentTypeSelected,
    },
  });

  const handleClose = () => {
    void navigate({
      to: "/locations/$id/devices/$deviceId",
      params: { id: locationId, deviceId },
    });
  };

  const handleSave = () => {
    try {
      const values = form.getValues();
      // Store in session storage for the parent route to pick up
      writePendingPosDeviceTerminalConfig(deviceId, {
        ...values,
        type: paymentTypeSelected,
      });
      toast.success("Payment terminal details added", {
        description: "Click 'Save Changes' to complete the configuration",
      });
      handleClose();
    } catch (error) {
      toast.error("Failed to add payment terminal details");
      logger.exception("Error saving terminal config", error);
    }
  };

  if (isPending || !device) {
    return <DeviceRouteLoadingDialog onOpenChange={handleClose} />;
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-2xl space-y-5">
        <DialogHeader className="space-y-1">
          <DialogTitle className="h2 font-heading text-foreground text-center">
            Add Payment Terminal
          </DialogTitle>
          <DialogDescription className="text-foreground text-center">
            Select a payment terminal provider for this POS device.
          </DialogDescription>
        </DialogHeader>
        {step === 0 ? (
          <>
            <div className="flex w-full space-x-4">
              {paymentTypes.map((paymentType) => (
                <button
                  className={twJoin(
                    "relative rounded-xl border border-border p-2.5",
                    paymentType === paymentTypeSelected
                      ? "border-2 border-foreground bg-background"
                      : "bg-muted"
                  )}
                  disabled={paymentTypeOptions[paymentType].comingSoon}
                  key={paymentType}
                  onClick={() => {
                    setPaymentTypeSelected(paymentType);
                    form.reset({
                      ...form.getValues(),
                      type: paymentType,
                    });
                  }}
                  type="button"
                >
                  <div className="flex h-44 w-60 items-center justify-center">
                    {paymentTypeOptions[paymentType].comingSoon ? (
                      <Badge
                        className="absolute top-3 left-3 px-2 py-1"
                        variant="information"
                      >
                        Coming soon
                      </Badge>
                    ) : null}
                    <img
                      alt={`${paymentTypeOptions[paymentType].title} logo`}
                      height={paymentTypeOptions[paymentType].height}
                      src={paymentTypeOptions[paymentType].logo}
                      style={{ maxWidth: "100%", height: "auto" }}
                      width={paymentTypeOptions[paymentType].width}
                    />
                  </div>
                </button>
              ))}
            </div>
            <Button className="w-full px-5 py-3" onClick={() => setStep(1)}>
              Next
            </Button>
          </>
        ) : null}
        {step === 1 ? (
          <Form {...form}>
            <form className="space-y-5">
              <div className="bg-muted flex w-full items-center space-x-3 rounded-2xl px-4 py-3">
                <img
                  alt={`${paymentTypeOptions[paymentTypeSelected].title} logo`}
                  height={24}
                  src={paymentTypeOptions[paymentTypeSelected].logo}
                  style={{ width: "auto", height: "24px" }}
                  width={42}
                />
                <p className="label text-foreground leading-tight">
                  {paymentTypeOptions[paymentTypeSelected].title}
                </p>
              </div>
              <FormField
                control={form.control}
                name="locationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stripe Terminal Location ID</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex w-full justify-between space-x-3">
                <Button
                  className="w-full"
                  onClick={() => {
                    form.reset({
                      ...EMPTY_POS_DEVICE_PAYMENT_TERMINAL,
                      type: paymentTypeSelected,
                    });
                    setStep(0);
                  }}
                  type="button"
                  variant="outline"
                >
                  Back
                </Button>
                <Button className="w-full" onClick={handleSave} type="button">
                  Save Terminal Details
                </Button>
              </div>
            </form>
          </Form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
